import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, API_BASE } from "@/lib/api";
import { money } from "@/lib/format";
import { HardHat, Loader2, Lock, Download, CheckCircle2, MessageSquare, Send, XCircle, Boxes, Eye, Move3d, Sofa } from "lucide-react";
import { FloorplanViewer, NEW_PHASES, REMODEL_PHASES, PHASE_LABEL } from "@/components/three/FloorplanViewer";

export default function ProposalPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [needsPw, setNeedsPw] = useState(false);
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [floorplan, setFloorplan] = useState(null);
  const [phase, setPhase] = useState("finish");
  const [walk, setWalk] = useState(false);
  const [furnished, setFurnished] = useState(false);

  const load = useCallback(async (password) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/public/proposal/${token}`, { params: password ? { pw: password } : {} });
      if (d.needs_password) { setNeedsPw(true); setData(null); }
      else {
        setNeedsPw(false); setData(d);
        api.get(`/public/proposal/${token}/floorplan`, { params: password ? { pw: password } : {} })
          .then(({ data: fp }) => { if (fp?.rooms?.length) setFloorplan(fp); })
          .catch(() => {});
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (!name.trim()) { toast.error("Enter your name to accept"); return; }
    setBusy(true);
    try { await api.post(`/public/proposal/${token}/accept`, { name, pw: pw || null }); toast.success("Proposal accepted — thank you!"); load(pw); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const comment = async () => {
    if (!name.trim() || !message.trim()) { toast.error("Add your name and a message"); return; }
    setBusy(true);
    try { await api.post(`/public/proposal/${token}/comment`, { name, message, pw: pw || null }); setMessage(""); toast.success("Comment sent"); load(pw); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const decide = async (rid, action) => {
    if (!name.trim()) { toast.error("Enter your name (in the Accept box) first"); return; }
    setBusy(true);
    try { await api.post(`/public/proposal/${token}/revision/${rid}`, { action, name, pw: pw || null }); toast.success(`Change ${action === "approve" ? "approved" : "rejected"}`); load(pw); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (needsPw) {
    return (
      <div className="min-h-screen dark bg-background text-foreground grid place-items-center p-4">
        <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 text-center">
          <Lock className="h-8 w-8 mx-auto text-primary mb-3" />
          <h1 className="font-bold text-lg mb-1">Protected proposal</h1>
          <p className="text-sm text-muted-foreground mb-4">Enter the password shared with you.</p>
          <input data-testid="public-pw-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 mb-3 outline-none focus:border-primary" />
          <button data-testid="public-pw-submit" onClick={() => load(pw)} className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 transition-colors">View proposal</button>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const p = data.project || {};
  const t = data.totals || {};
  const co = data.company || {};
  const hasBrand = co.logo_b64 || co.name;
  const pdfUrl = `${API_BASE}/public/proposal/${token}/pdf${pw ? `?pw=${encodeURIComponent(pw)}` : ""}`;

  return (
    <div className="min-h-screen dark bg-background text-foreground">
      <header className="h-16 flex items-center justify-between px-5 sm:px-8 border-b border-border">
        <div className="flex items-center gap-2" data-testid="public-brand">
          {hasBrand ? (
            <>
              {co.logo_b64
                ? <img src={co.logo_b64} alt={co.name || "Company"} className="h-9 max-w-[160px] object-contain" />
                : <div className="h-9 w-9 rounded-lg bg-primary grid place-items-center"><HardHat className="h-5 w-5 text-primary-foreground" /></div>}
              {co.name && <span className="font-extrabold tracking-tight">{co.name}</span>}
            </>
          ) : (
            <><div className="h-9 w-9 rounded-lg bg-primary grid place-items-center"><HardHat className="h-5 w-5 text-primary-foreground" /></div><span className="font-extrabold tracking-tight">SmartBuild <span className="text-primary">AI</span></span></>
          )}
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="public-download-pdf" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary transition-colors"><Download className="h-4 w-4" />PDF</a>
      </header>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Construction Proposal</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{p.name}</h1>
        <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-sm">
          <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Prepared for</div><div className="font-semibold">{p.client || "—"}</div></div>
          {(co.name || co.address || co.license) && (
            <div data-testid="public-company-info"><div className="text-[10px] font-mono uppercase text-muted-foreground">Prepared by</div><div className="font-semibold">{co.name || co.prepared_by || "—"}</div>{co.address && <div className="text-xs text-muted-foreground whitespace-pre-line">{co.address}</div>}{co.license && <div className="text-xs text-primary font-medium mt-0.5">Licensed Contractor · #{co.license}</div>}</div>
          )}
          <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Type</div><div className="font-semibold capitalize">{p.type || "—"}</div></div>
        </div>
        {p.description && <p className="mt-5 text-muted-foreground leading-relaxed">{p.description}</p>}

        {data.status === "accepted" && (
          <div className="mt-6 flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-emerald-500" data-testid="public-accepted-banner">
            <CheckCircle2 className="h-5 w-5" /> Accepted by {data.accepted_by}
          </div>
        )}

        <div className="mt-8 rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border"><th className="px-4 py-3">CSI</th><th className="px-4 py-3">Section</th><th className="px-4 py-3 text-right">Amount</th></tr></thead>
            <tbody>
              {(data.sections || []).map((s, i) => (
                <tr key={i} className="border-b border-border/50"><td className="px-4 py-2.5 font-mono text-xs text-primary">{s.code}</td><td className="px-4 py-2.5">{s.title}</td><td className="px-4 py-2.5 text-right font-mono tabular-nums">{money(s.subtotal)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 rounded-xl border border-border bg-card p-5 space-y-1.5 text-sm">
          {[["Base subtotal", t.base_subtotal], ["Overhead", t.overhead_amount], ["Profit", t.profit_amount], ["Contingency", t.contingency_amount], ["Tax / VAT", t.tax_amount]].map(([l, v]) => (
            <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-mono tabular-nums">{money(v)}</span></div>
          ))}
          {data.savings && Number(data.savings.amount) > 0 && (
            <div className="flex justify-between items-center -mx-5 px-5 py-2 my-1 bg-emerald-500/10 border-y border-emerald-500/20" data-testid="public-savings-line">
              <span className="font-semibold text-emerald-500">Value Engineering Savings</span>
              <span className="font-mono font-bold text-emerald-500 tabular-nums">− {money(data.savings.amount)}{data.savings.pct ? ` (${data.savings.pct}%)` : ""}</span>
            </div>
          )}
          <div className="flex justify-between pt-3 mt-2 border-t border-border"><span className="font-bold">Grand Total</span><span className="font-mono font-bold text-primary text-xl tabular-nums" data-testid="public-grand-total">{money(t.grand_total)}</span></div>
        </div>

        {/* 3D walkthrough */}
        {floorplan && (
          <div className="mt-8 rounded-xl border border-border bg-card p-5" data-testid="public-3d-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <span className="font-bold flex items-center gap-2"><Boxes className="h-4 w-4 text-primary" /> 3D Walkthrough</span>
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center rounded-lg border border-border overflow-hidden">
                  {(floorplan.project_kind === "remodel" ? REMODEL_PHASES : NEW_PHASES).map((p) => (
                    <button key={p} data-testid={`public-phase-${p}`} onClick={() => setPhase(p)}
                      className={`px-2.5 py-1 text-xs font-semibold capitalize ${phase === p ? "bg-amber-500 text-black" : "hover:bg-secondary"}`}>{PHASE_LABEL[p]}</button>
                  ))}
                </div>
                <button data-testid="public-toggle-walk" onClick={() => setWalk((w) => !w)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${walk ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                  {walk ? <Move3d className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {walk ? "Walk" : "Orbit"}
                </button>
                <button data-testid="public-toggle-furnish" onClick={() => setFurnished((f) => !f)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold ${furnished ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                  <Sofa className="h-3.5 w-3.5" /> {furnished ? "Furnished" : "Furnish"}
                </button>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden bg-[#0b1220]" style={{ height: 420 }}>
              <FloorplanViewer floorplan={floorplan} phase={phase} walk={walk} furnished={furnished} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Drag to orbit · scroll to zoom{walk ? " · WASD to walk, click to look around" : ""}. This is an illustrative 3D approximation.</p>
          </div>
        )}

        {/* Change orders / revisions */}
        {(data.revisions || []).length > 0 && (
          <div className="mt-8 rounded-xl border border-border bg-card p-5" data-testid="public-revisions">
            <h3 className="font-semibold mb-1">Change orders</h3>
            <p className="text-xs text-muted-foreground mb-4">Review and approve changes to the original scope. Approved changes adjust the contract value.</p>
            <div className="space-y-3">
              {data.revisions.map((r) => (
                <div key={r.id} className="rounded-lg border border-border/60 p-4" data-testid={`public-revision-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-mono text-primary">{r.co_number}</div>
                      <div className="text-sm font-medium">{r.change_text}</div>
                      {r.summary && <div className="text-xs text-muted-foreground mt-0.5">{r.summary}</div>}
                    </div>
                    <div className={`font-mono font-bold shrink-0 ${r.net_delta >= 0 ? "text-destructive" : "text-emerald-500"}`}>{r.net_delta >= 0 ? "+" : "−"}{money(Math.abs(r.net_delta))}</div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    {r.client_status === "approved" ? (
                      <span className="text-xs font-semibold text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" />Approved{r.decided_by ? ` by ${r.decided_by}` : ""}</span>
                    ) : r.client_status === "rejected" ? (
                      <span className="text-xs font-semibold text-destructive inline-flex items-center gap-1"><XCircle className="h-4 w-4" />Rejected</span>
                    ) : (
                      <>
                        <button data-testid={`public-reject-${r.id}`} onClick={() => decide(r.id, "reject")} disabled={busy} className="rounded-lg border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50">Reject</button>
                        <button data-testid={`public-approve-${r.id}`} onClick={() => decide(r.id, "approve")} disabled={busy} className="rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-sm font-bold hover:bg-orange-600 transition-colors disabled:opacity-50">Approve</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-semibold mb-3">Accept proposal</h3>
            <input data-testid="public-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 mb-3 outline-none focus:border-primary" />
            <button data-testid="public-accept-btn" onClick={accept} disabled={busy || data.status === "accepted"} className="w-full rounded-lg bg-primary text-primary-foreground py-2.5 font-bold hover:bg-orange-600 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4" />{data.status === "accepted" ? "Accepted" : "Accept & sign"}</button>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" />Leave a comment</h3>
            <textarea data-testid="public-comment-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder="Questions or feedback…" className="w-full rounded-lg border border-input bg-popover px-3 py-2.5 mb-3 outline-none focus:border-primary resize-none" />
            <button data-testid="public-comment-btn" onClick={comment} disabled={busy} className="w-full rounded-lg border border-border py-2.5 font-semibold hover:bg-secondary transition-colors inline-flex items-center justify-center gap-2"><Send className="h-4 w-4" />Send comment</button>
          </div>
        </div>

        {(data.comments || []).length > 0 && (
          <div className="mt-6 rounded-xl border border-border bg-card p-5">
            <h3 className="font-semibold mb-3">Conversation</h3>
            <div className="space-y-3">
              {data.comments.map((c, i) => (
                <div key={i} className="border-l-2 border-primary/40 pl-3"><div className="text-sm font-medium">{c.name}</div><div className="text-sm text-muted-foreground">{c.message}</div></div>
              ))}
            </div>
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground mt-10">Powered by SmartBuild AI Estimator</p>
      </div>
    </div>
  );
}
